import React, { FC, useEffect, useState, useCallback, useRef } from "react";
import { AxiosError } from "axios";
import { Button, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import useSpinnerLoader from "../../../../shared/components/spinner-loader";
import TableHeaderOrdering, {
  Header,
  OrderData,
} from "../../../../shared/components/table-header-ordering";
import { notificationService } from "../../../../shared/services/notification-service";
import TableRowNoData from "../../../../shared/components/molecules/commons/table-row-no-data";
import { HrOnlineService } from "../../../../shared/services/Report/HrOnline/hr-online";
import {
  Document,
  Page,
  View,
  Text,
  PDFDownloadLink,
  Font,
  StyleSheet,
} from "@react-pdf/renderer";

import moment from "moment";
// Register fonts to fix loading issues
Font.register({
  family: "kanit-regular",
  src: "https://fonts.gstatic.com/s/kanit/v1/L6VKvM17ZmevDynOiw7H9w.ttf",
});
Font.register({
  family: "kanit-200",
  src: "https://fonts.gstatic.com/s/kanit/v1/wfLWkj1C4tYl7MoiFWS3bA.ttf",
});

const thaiMonths = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

const ReportRetirementTrendNext3YearsListPage: FC = () => {
  const [queryParams, setQueryParams] = useState<any>({
    ordering_field: "",
    ordering: "",
    page: 1,
    page_size: 10,
    year: moment().year() + 543,
  });
  const [data, setData] = useState<any>([]);
  const [dataExport, setDataExport] = useState<any>([]);
  const { loaderComponent, startLoading, stopLoading } = useSpinnerLoader();
  const loadingRef = useRef({ startLoading, stopLoading });
  loadingRef.current = { startLoading, stopLoading };

  const headers: Header[] = [
    {
      label: "ที่",
      field: "index",
      order: null,
      isOrder: true,
    },
    {
      label: "ส่วนนราชการ/ตำแหน่งทางสายงาน",
      field: "name",
      order: null,
      isOrder: true,
    },
    {
      label: "กรอบอัตรากำลัง",
      field: "main_power_rate",
      order: null,
      isOrder: true,
    },
    {
      label: "อัตราว่าง",
      field: "vacant_positions",
      order: null,
      isOrder: true,
    },
    {
      label: "บรรจุจริง",
      field: "actual_filled",
      order: null,
      isOrder: true,
    },
    {
      label: (moment().year() + 543).toString(),
      field: "retire_current",
      order: null,
      isOrder: true,
    },
    {
      label: String(moment().year() + 544),
      field: "retire_next1",
      order: null,
      isOrder: true,
    },
    {
      label: String(moment().year() + 545),
      field: "retire_next2",
      order: null,
      isOrder: true,
    },
  ];

  const fetchData = useCallback(
    async (params: any) => {
      loadingRef.current.startLoading();
      try {
        const result =
          await HrOnlineService.getReportRetirementTrendNext3YearsList(params);
        const resultAll =
          await HrOnlineService.getReportRetirementTrendNext3YearsList({
            ...params,
            page_size: 10000,
            page: 1,
          });
        setData(result);
        setDataExport(resultAll);
      } catch (error) {
        const err = error as AxiosError;
        notificationService.dangerNotification(
          "โหลดข้อมูลไม่สำเร็จ",
          err.message
        );
      } finally {
        loadingRef.current.stopLoading();
      }
    },
    [queryParams.ordering_field, queryParams.ordering]
  );

  const tableRowNoData = () => {
    if (data.rows?.length === 0) {
      return <TableRowNoData colspan={9} />;
    }
    return null;
  };

  useEffect(() => {
    fetchData(queryParams);
  }, [fetchData, queryParams]);

  return (
    <>
      <div className="d-flex justify-content-between py-4">
        <h2 className="text-mof-primary m-0">
          รายงานแนวโน้มการเกษียณของตำแหน่งเป้าหมายอายุในปีถัดไป 3 ปี ข้างหน้า
        </h2>
      </div>
      {loaderComponent}
      <div className="row">
        {/* <label className="form-label">ปี</label>
        <div className="col-sm-12 col-md-2">
          <input
            className="form-control"
            type="text"
            value={queryParams.year ? queryParams.year : ""}
            onChange={handleYearChange}
            name="search"
          />
        </div>
        <div className="col-sm-12 col-md-4">
          <Button
            variant="primary"
            type="button"
            className="d-inline-block px-4"
            onClick={() => fetchData(queryParams)}
          >
            <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
          </Button>
        </div> */}
        <div className="col-sm-12 col-md-12 d-flex justify-content-end">
          <PDFDownloadLink
            document={<PDF data={dataExport} />}
            fileName={`รายงานผู้เกษียณอายุในปี_${queryParams.year}.pdf`}
          >
            {
              (({ loading }: any) => (
                <Button
                  variant="primary"
                  className="btn-action btn-mof-primary "
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faFileExport} className="me-2" />
                  {loading ? "Loading..." : "Export"}
                </Button>
              )) as any
            }
          </PDFDownloadLink>
        </div>
      </div>

      <div className="overflow-auto mt-3">
        <Table striped bordered hover>
          <thead>
            <TableHeaderOrdering
              headers={headers}
              onChangeOrder={(orderData: OrderData) => {
                setQueryParams({
                  ...queryParams,
                  ordering_field: orderData.field,
                  ordering: orderData.order,
                });
              }}
              isShow={true}
            />
          </thead>
          <tbody>
            {data.rows?.map((department: any, departmentIndex: number) => (
              <React.Fragment key={`dept-${departmentIndex}`}>
                {/* Department row */}
                <ReportRetirementTrendNext3YearsRow
                  {...department}
                  index={departmentIndex + 1}
                  isParentRow={true}
                />

                {/* Division Groups */}
                {department.division_group &&
                  typeof department.division_group === "object" &&
                  Object.keys(department.division_group).map(
                    (divKey: string, divIndex: number) => {
                      const divisionData = department.division_group[divKey];
                      return divisionData ? (
                        <React.Fragment
                          key={`div-${departmentIndex}-${divIndex}`}
                        >
                          {/* Division row */}
                          <ReportRetirementTrendNext3YearsRow
                            {...divisionData}
                            index={`${departmentIndex + 1}.${divIndex + 1}`}
                            isParentRow={true}
                          />

                          {/* Position rows under division */}
                          {divisionData?.positions &&
                            Array.isArray(divisionData.positions) &&
                            divisionData.positions.map(
                              (position: any, posIndex: number) => (
                                <ReportRetirementTrendNext3YearsRow
                                  key={`pos-${departmentIndex}-${divIndex}-${posIndex}`}
                                  {...position}
                                  index={posIndex + 1}
                                  isParentRow={false}
                                />
                              )
                            )}

                          {/* Summary for division */}
                          {divisionData?.summary && (
                            <ReportRetirementTrendNext3YearsRow
                              {...divisionData.summary}
                              index={`${departmentIndex + 1}.${divIndex + 1}`}
                              type={`${
                                divisionData?.division_group_name || ""
                              }`}
                              isParentRow={false}
                            />
                          )}
                        </React.Fragment>
                      ) : null;
                    }
                  )}

                {/* Positions directly under department (if any) */}
                {department.positions &&
                  Array.isArray(department.positions) &&
                  department.positions.map(
                    (position: any, posIndex: number) => (
                      <ReportRetirementTrendNext3YearsRow
                        key={`pos-${departmentIndex}-direct-${posIndex}`}
                        {...position}
                        index={posIndex + 1}
                        isParentRow={false}
                      />
                    )
                  )}

                {/* Department summary */}
                {department.summary && !department.division_group && (
                  <ReportRetirementTrendNext3YearsRow
                    {...department.summary}
                    index={`${departmentIndex + 1}`}
                    type={`${
                      department.department_office_name ||
                      department.division_group_name ||
                      department.manager_name ||
                      ""
                    }`}
                    isParentRow={false}
                  />
                )}
              </React.Fragment>
            ))}

            {tableRowNoData()}
          </tbody>
        </Table>
      </div>
    </>
  );
};

const ReportRetirementTrendNext3YearsRow: FC<any> = ({
  index,
  structure_position_name,
  division_group_name,
  department_office_name,
  manager_name,
  allocated_power_rate,
  actual_filled,
  retire_current,
  retire_next1,
  retire_next2,
  isParentRow,
  type = null,
}) => {
  // Get the display name based on hierarchy level
  const getDisplayName = () => {
    if (typeof type === "string") return type;
    if (structure_position_name) return structure_position_name;
    if (division_group_name) return division_group_name;
    if (department_office_name) return department_office_name;
    if (manager_name) return manager_name;
    return "";
  };

  // Determine if this is a parent row (department or division) if not explicitly set
  const isParentRowFinal =
    isParentRow !== undefined
      ? isParentRow
      : !structure_position_name &&
        (department_office_name || division_group_name || manager_name);

  return (
    <tr>
      <td>{type || isParentRowFinal ? "" : index}</td>
      <td>{getDisplayName()}</td>
      <td>{!isParentRowFinal ? allocated_power_rate || 0 : ""}</td>
      <td>
        {!isParentRowFinal
          ? (allocated_power_rate || 0) - (actual_filled || 0)
          : ""}
      </td>
      <td>{!isParentRowFinal ? actual_filled || 0 : ""}</td>
      <td>{!isParentRowFinal ? retire_current || "0" : ""}</td>
      <td>{!isParentRowFinal ? retire_next1 || "0" : ""}</td>
      <td>{!isParentRowFinal ? retire_next2 || "0" : ""}</td>
    </tr>
  );
};

// PDF Component for export
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "kanit-regular",
  },
  header: {
    fontSize: 13,
    marginBottom: 5,
    textAlign: "center",
    fontFamily: "kanit-regular",
  },
  headerTitle: {
    fontSize: 18,
    marginBottom: 10,
    textAlign: "center",
    fontFamily: "kanit-regular",
  },
  table: {
    display: "flex",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableColHeader: {
    width: "12.5%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
    backgroundColor: "#f9f9f9",
    fontSize: 10,
  },
  tableColHeaderWide: {
    width: "25%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
    backgroundColor: "#f9f9f9",
    fontSize: 10,
  },
  tableCol: {
    width: "12.5%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
    fontSize: 10,
  },
  tableColWide: {
    width: "25%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
    fontSize: 10,
  },
  footer: {
    marginTop: 20,
    fontSize: 10,
    textAlign: "right",
  },
  parentRow: {
    backgroundColor: "#f9f9f9",
  },
  summaryRow: {
    backgroundColor: "#e6e6e6",
    fontWeight: "bold",
  },
});

const PDF: FC<{ data: any }> = ({ data }) => {
  // Format current date in Thai
  const thaiDate =
    moment().format("D") +
    " " +
    thaiMonths[moment().month()] +
    " " +
    (moment().year() + 543);
  const currentYear = moment().year() + 543;

  // Log data for debugging
  console.log("PDF Data:", data);

  // Helper to check if an item is a department
  const isDepartment = (item: any) => {
    return item && item.department_office_name && !item.structure_position_name;
  };

  // Helper to check if an item is a division
  const isDivision = (item: any) => {
    return item && item.division_group_name && !item.structure_position_name;
  };

  // Helper to process data into a flat array for rendering
  const processData = () => {
    if (!data) return [];

    // Check if data is already a flat array
    if (Array.isArray(data) && data.length > 0 && !data[0].rows) {
      return data;
    }

    // If data is in hierarchical structure with rows property
    const flatData: any[] = [];

    if (data.rows && Array.isArray(data.rows)) {
      data.rows.forEach((department: any, deptIndex: number) => {
        // Add department
        if (department.department_office_name || department.manager_name) {
          flatData.push({
            ...department,
            isParent: true,
            index: deptIndex + 1,
            type: "department",
          });
        }

        // Add positions directly under department
        if (department.positions && Array.isArray(department.positions)) {
          department.positions.forEach((position: any, posIndex: number) => {
            flatData.push({
              ...position,
              index: `${posIndex + 1}`,
              isParent: false,
              type: "position",
            });
          });
        }

        // Add department summary if available
        if (department.summary && !department.division_group) {
          flatData.push({
            ...department.summary,
            structure_position_name:
              department.department_office_name ||
              department.manager_name ||
              "",
            index: deptIndex + 1,
            isParent: false,
            type: "summary",
          });
        }

        // Process division groups
        if (
          department.division_group &&
          typeof department.division_group === "object"
        ) {
          Object.keys(department.division_group).forEach(
            (divKey: string, divIndex: number) => {
              const division = department.division_group[divKey];

              if (division) {
                // Add division
                flatData.push({
                  ...division,
                  isParent: true,
                  index: `${divIndex + 1}`,
                  type: "division",
                });

                // Add positions under division
                if (division.positions && Array.isArray(division.positions)) {
                  division.positions.forEach(
                    (position: any, posIndex: number) => {
                      flatData.push({
                        ...position,
                        index: `${posIndex + 1}`,
                        isParent: false,
                        type: "position",
                      });
                    }
                  );
                }

                // Add division summary
                if (division.summary) {
                  flatData.push({
                    ...division.summary,
                    structure_position_name: division.division_group_name || "",
                    index: `${divIndex + 1}`,
                    isParent: false,
                    type: "summary",
                  });
                }
              }
            }
          );
        }
      });
    }

    return flatData.length > 0 ? flatData : Array.isArray(data) ? data : [];
  };

  const processedData = processData();

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page} wrap>
        <View>
          {/* Organization Header */}
          <Text style={styles.headerTitle}>องค์การตลาดเพื่อเกษตรกร</Text>

          {/* Report Title */}
          <Text style={styles.headerTitle}>
            รายงานแนวโน้มการเกษียณของตำแหน่งเป้าหมายอายุในปีถัดไป 3 ปี ข้างหน้า
          </Text>

          {/* Current Date */}
          <Text style={styles.headerTitle}>ประจำวันที่ {thaiDate}</Text>
        </View>

        <View style={styles.table}>
          {/* Table Header - Fixed on every page */}
          <View style={styles.tableRow} fixed>
            <View style={styles.tableColHeader}>
              <Text>ที่</Text>
            </View>
            <View style={styles.tableColHeaderWide}>
              <Text>ส่วนราชการ/ตำแหน่งทางสายงาน</Text>
            </View>
            <View style={styles.tableColHeader}>
              <Text>กรอบอัตรากำลัง</Text>
            </View>
            <View style={styles.tableColHeader}>
              <Text>อัตราว่าง</Text>
            </View>
            <View style={styles.tableColHeader}>
              <Text>บรรจุจริง</Text>
            </View>
            <View style={styles.tableColHeader}>
              <Text>{currentYear}</Text>
            </View>
            <View style={styles.tableColHeader}>
              <Text>{currentYear + 1}</Text>
            </View>
            <View style={styles.tableColHeader}>
              <Text>{currentYear + 2}</Text>
            </View>
          </View>

          {/* Table Data */}
          {processedData.map((item: any, index: number) => {
            const isParent =
              item.isParent || isDepartment(item) || isDivision(item);
            const isSummary = item.type === "summary";

            // Fix: Use View's style prop correctly - it accepts a style object, not an array
            const rowStyle = Object.assign(
              {},
              styles.tableRow,
              isParent ? styles.parentRow : {},
              isSummary ? styles.summaryRow : {}
            );

            const displayName =
              item.structure_position_name ||
              item.division_group_name ||
              item.department_office_name ||
              item.manager_name ||
              "";

            // Get allocated and actual rates with fallbacks
            const allocatedRate =
              item.allocated_power_rate || item.main_power_rate || "";
            const actualRate = item.actual_filled || "";
            const vacantPositions =
              allocatedRate && actualRate
                ? (
                    parseFloat(allocatedRate) - parseFloat(actualRate)
                  ).toString()
                : "";

            return (
              <View style={rowStyle} key={`item-${index}`} wrap={false}>
                <View style={styles.tableCol}>
                  <Text>
                    {item.type === "summary" || isParent ? "" : item.index}
                  </Text>
                </View>
                <View style={styles.tableColWide}>
                  <Text>{displayName}</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text>{!isParent || isSummary ? allocatedRate : ""}</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text>{!isParent || isSummary ? vacantPositions : ""}</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text>{!isParent || isSummary ? actualRate : ""}</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text>
                    {!isParent || isSummary ? item.retire_current || "0" : ""}
                  </Text>
                </View>
                <View style={styles.tableCol}>
                  <Text>
                    {!isParent || isSummary ? item.retire_next1 || "0" : ""}
                  </Text>
                </View>
                <View style={styles.tableCol}>
                  <Text>
                    {!isParent || isSummary ? item.retire_next2 || "0" : ""}
                  </Text>
                </View>
              </View>
            );
          })}
        </View>
      </Page>
    </Document>
  );
};

export default ReportRetirementTrendNext3YearsListPage;
